import AnnouncementIcon from '@mui/icons-material/Announcement';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Checkbox, Divider, Typography, CircularProgress, Tooltip, Alert } from "@mui/material";
import Chip from '@mui/material/Chip';
import Moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { auditColumnDefs } from "../../constants/QuestionBankConstants";
import Grid from '../../platform-components/grid/Grid';
import { apiPaths, get, getInstutueApp, postInstituteAppData, putInstituteAppData } from "../api-integration-util";
import BasicTabs from "../basic-tabs/BasicTabs";
import { getUserDetails } from "../utils/shared-data.service";
import "./questionviewer.scss";
import { Card } from '@material-ui/core';
import TuneIcon from '@mui/icons-material/Tune';
import QuestionVariationsDialog from './QuestionVariationsDialog';
import { hasPermission } from '../utils/permission.service';
import { ResolvePanel, DetailPanel } from 'modules/answer-conflicts/AnswerConflicts';


const ClassificationVerificationSection = ({ questionId }) => {
  const [verifying, setVerifying] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  useEffect(() => {
    stopPolling();
    setVerifying(false);
    setConflictInfo(null);
    if (questionId) {
      checkExistingConflict(questionId);
    }
    return () => stopPolling();
  }, [questionId]);

  const checkExistingConflict = async (qId) => {
    try {
      const res = await getInstutueApp(
        apiPaths.classificationConflicts + "/question/" + qId,
        null,
        { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email }
      );
      if (res?.success && res?.hasConflict && res?.conflict) {
        if (res.conflict.status === 'PROCESSING') {
          setVerifying(true);
          pollForResult(qId);
        } else {
          setConflictInfo(res.conflict);
        }
      }
    } catch (e) {
      // Silent — conflict check is non-critical
    }
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
  };

  const pollForResult = (qId) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await getInstutueApp(
          apiPaths.classificationConflicts + "/question/" + qId,
          null,
          { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email }
        );
        if (res?.success && res?.conflict && res.conflict.status !== 'PROCESSING') {
          stopPolling();
          setConflictInfo(res.conflict);
          setVerifying(false);
        }
      } catch (e) {
        // Keep polling on transient errors
      }
    }, 5000);
    pollTimeoutRef.current = setTimeout(() => { stopPolling(); setVerifying(false); }, 180000);
  };

  const handleVerifyClassification = async () => {
    if (!questionId) return;
    setVerifying(true);
    try {
      const res = await postInstituteAppData(
        apiPaths.classificationConflictsVerify + "/" + questionId,
        { data: {}, headers: { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email } }
      );
      if (res?.success || res?.conflict) {
        const conflict = res.conflict;
        if (conflict && conflict.status === 'PROCESSING') {
          pollForResult(questionId);
        } else {
          setConflictInfo(conflict);
          setVerifying(false);
        }
      }
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Classification verification failed. Please try again.');
      setVerifying(false);
    }
  };

  const getStatusColor = () => {
    if (!conflictInfo) return null;
    if (conflictInfo.conflictType === 'ALL_AGREE') return 'success';
    if (conflictInfo.status === 'RESOLVED') return 'info';
    return 'warning';
  };

  const getStatusLabel = () => {
    if (!conflictInfo) return null;
    if (conflictInfo.conflictType === 'ALL_AGREE') return 'All Agree';
    if (conflictInfo.conflictType === 'DB_MISSING') return 'DB Missing';
    if (conflictInfo.status === 'RESOLVED') return 'Resolved';
    if (conflictInfo.status === 'PENDING') return conflictInfo.conflictType.replace(/_/g, ' ');
    return conflictInfo.status;
  };

  return (
    <div style={{ padding: "5px", marginTop: "8px" }}>
      <Divider style={{ marginBottom: "8px" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
        <strong style={{ fontSize: "12px" }}>AI Classification</strong>
      </div>

      {conflictInfo && (
        <div style={{ marginBottom: "6px" }}>
          <Chip
            label={getStatusLabel()}
            size="small"
            color={getStatusColor()}
            style={{ fontSize: "10px", height: "20px", marginBottom: "4px" }}
          />
          {conflictInfo.primarySubjectName && (
            <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
              AI: {conflictInfo.primarySubjectName}
              {conflictInfo.primaryTopicName && ` / ${conflictInfo.primaryTopicName}`}
            </div>
          )}
        </div>
      )}

      {!verifying && hasPermission('QA Actions', 'Verify Classification') && (
        <Button size="small" variant="outlined"
          startIcon={<TuneIcon style={{ fontSize: 14 }} />}
          onClick={handleVerifyClassification}
          style={{
            textTransform: 'none', fontSize: '11px',
            borderColor: '#6366f1', color: '#6366f1',
            padding: '2px 8px'
          }}>
          {conflictInfo ? 'Re-verify' : 'Verify Classification'}
        </Button>
      )}
      {verifying && (
        <Button size="small" variant="outlined" disabled
          startIcon={<CircularProgress size={12} />}
          style={{ textTransform: 'none', fontSize: '11px', padding: '2px 8px' }}>
          Verifying...
        </Button>
      )}
    </div>
  );
};

const tabList = (props) => [
  {
    label: 'Tags',
    content: <>

      <div
        style={{
          marginTop: "10px",
          display: "flex",
          justifyContent: "flex-start",
          marginBottom: "20px",
          flexWrap: "wrap",
          marginLeft: '15px'
        }}
      >
        <Card style={{ maxWidth: "270px" }}>
          <div style={{ marginLeft: "5px" }}>
            {props?.question_course_map?.length > 0 &&
              <div style={{padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Course</strong>
                  {(props?.question_course_map || []).map((el) => {
                    return (
                      <Chip label={el?.course} color="error" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                    );
                  })}
                </div>
              </div>
            }

            {props?.question_tag_map?.length > 0 &&
              <div style={{ padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Tag</strong>
                  {(props?.question_tag_map || []).map((el) => {
                    return (
                      <Chip label={el?.tagName} color="error" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                    );
                  })}
                </div>
              </div>
            }

            {props?.question_topic_map?.length > 0 &&
              <div style={{ padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Topic</strong>
                  {(props?.question_topic_map || []).map((el) => {
                    return (
                      <Chip label={el?.topic} color="error" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                    );
                  })}
                </div>
              </div>
            }

            {props?.question_subtopic_map?.length > 0 &&
              <div style={{ padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Subtopic</strong>
                  {(props?.question_subtopic_map || []).map((el) => {
                    return (
                      <Chip label={el?.subtopicName} color="info" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                    );
                  })}
                </div>
              </div>
            }

            {props?.questionType &&
              <div style={{padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Complexity</strong>
                  <Chip label={props?.questionType} size="small" color="primary" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                </div>
              </div>
            }

            {props?.subject &&
              <div style={{ padding: "5px" }}>
                <div style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <strong style={{ marginRight: "10px" }}>Subject</strong>
                  <Chip label={props?.subject} size="small" color="secondary" style={{ marginBottom: "5px", marginRight: "3px" }} ></Chip>
                </div>
              </div>
            }

            <ClassificationVerificationSection questionId={props?.questionId} />
          </div>
        </Card>
      </div>

    </>
  }, {
    label: 'Audits',
    content: <><div style={{ width: "100%", padding: "10px" }}>
      <div className="">
        <p>
          <Typography variant="overline" gutterBottom>
            Comments:
          </Typography>
        </p>
        <p>
          <Typography variant="caption" gutterBottom>
            Created by:{" "}
            <span style={{ color: "blue", textDecoration: "underline" }}>
              {props?.createdBy}
            </span>
          </Typography>
        </p>
        <p>
          <Typography variant="caption" gutterBottom>
            Created date: {Moment(props?.createdDate).format("DD-MM-YYYY")}
          </Typography>
        </p>
        <p>
          <Typography variant="caption" gutterBottom>
            Updated by:{" "}
            <span style={{ color: "blue", textDecoration: "underline" }}>
              {props?.updatedBy}
            </span>
          </Typography>
        </p>
        <p>
          <Typography variant="caption" gutterBottom>
            Updated date:{Moment(props?.updatedDate).format("DD-MM-YYYY")}
          </Typography>
        </p>
      </div>
    </div></>
  }
]

const AIExplanationSection = ({ questionId, onShowResolve, onShowDetail, refreshRef }) => {
  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Reset state when question changes + cleanup polling on unmount
  useEffect(() => {
    stopPolling();
    setVerifying(false);
    setConflictInfo(null);
    if (questionId) {
      checkExistingConflict(questionId);
    }
    return () => stopPolling();
  }, [questionId]);

  const checkExistingConflict = async (qId) => {
    try {
      const res = await getInstutueApp(
        apiPaths.answerConflicts + "/question/" + qId,
        null,
        { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email }
      );
      if (res?.success && res?.hasConflict && res?.conflict) {
        if (res.conflict.status === 'PROCESSING') {
          setVerifying(true);
          pollForResult(qId);
        } else {
          setConflictInfo(res.conflict);
        }
      }
    } catch (e) {
      // Silent — conflict check is non-critical
    }
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
  };

  const pollForResult = (qId) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await getInstutueApp(
          apiPaths.answerConflicts + "/question/" + qId,
          null,
          { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email }
        );
        if (res?.success && res?.conflict && res.conflict.status !== 'PROCESSING') {
          stopPolling();
          setConflictInfo(res.conflict);
          setVerifying(false);
        }
      } catch (e) {
        // Keep polling on transient errors
      }
    }, 5000);
    pollTimeoutRef.current = setTimeout(() => { stopPolling(); setVerifying(false); }, 180000);
  };

  const handleVerifyAnswer = async () => {
    if (!questionId) return;
    setVerifying(true);
    try {
      const res = await postInstituteAppData(
        apiPaths.answerConflictsVerify + "/" + questionId,
        { data: {}, headers: { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email } }
      );
      if (res?.success && res?.conflict) {
        if (res.conflict.status === 'PROCESSING') {
          pollForResult(questionId);
        } else {
          setConflictInfo(res.conflict);
          setVerifying(false);
          // Auto-show the appropriate inline panel
          if (res.conflict.status === 'PENDING') {
            onShowResolve(res.conflict);
          } else {
            onShowDetail(res.conflict);
          }
        }
      }
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Verification failed. Please try again.');
      setVerifying(false);
    }
  };

  const isPending = conflictInfo && conflictInfo.status === 'PENDING';
  const isResolved = conflictInfo && (conflictInfo.status === 'RESOLVED');

  // Expose checkExistingConflict for parent to call after resolve/detail panel closes
  useEffect(() => {
    if (refreshRef) refreshRef.current = () => checkExistingConflict(questionId);
  });

  return (
    <div className="ai-explanation-section">
      {/* Conflict warning banner */}
      {isPending && conflictInfo.conflictType !== 'ALL_AGREE' && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 1 }}
          action={
            <Button size="small" color="inherit" onClick={() => onShowResolve(conflictInfo)}>
              Review & Resolve
            </Button>
          }>
          Answer conflict detected ({conflictInfo.conflictType}).
          DB: {conflictInfo.dbAnswer}
          {conflictInfo.primaryAnswer && ` | Primary: ${conflictInfo.primaryAnswer}`}
          {conflictInfo.secondaryAnswer && ` | Secondary: ${conflictInfo.secondaryAnswer}`}
        </Alert>
      )}
      {isResolved && conflictInfo.conflictType === 'ALL_AGREE' && (
        <Alert severity={conflictInfo.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' ? 'warning' : 'success'}
          icon={<CheckCircleIcon />} sx={{ mb: 1 }}
          action={
            <Button size="small" color="inherit" onClick={() => onShowDetail(conflictInfo)}>
              View Details
            </Button>
          }>
          Answer verified — all sources agree. Answer: {conflictInfo.resolvedAnswer || conflictInfo.dbAnswer}
          {conflictInfo.resolvedBy === 'SYSTEM_AUTO_CONTENT_PRESERVED' && (
            <>
              {' — Explanation NOT updated (contains images or tables). Review and update manually.'}
              <Chip label="Images/Tables Preserved" size="small" color="warning"
                sx={{ ml: 1, fontSize: 10, height: 20, fontWeight: 'bold' }} />
            </>
          )}
        </Alert>
      )}
      {isResolved && conflictInfo.conflictType !== 'ALL_AGREE' && (
        <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 1 }}
          action={
            <Button size="small" color="inherit" onClick={() => onShowDetail(conflictInfo)}>
              View Details
            </Button>
          }>
          Conflict resolved. Answer: {conflictInfo.resolvedAnswer} (by {conflictInfo.resolvedBy})
        </Alert>
      )}

      {/* Verify Answer button + Re-verify */}
      <div className="ai-explanation-header">
        <div className="ai-explanation-title">
          <VerifiedUserIcon style={{ fontSize: 18, marginRight: 6, color: '#7c3aed' }} />
          <strong>Answer Verification</strong>
        </div>
        <div className="ai-explanation-actions">
          {!verifying && hasPermission('QA Actions', 'Verify Answer') && (
            <Tooltip title={conflictInfo ? 'Re-run verification against AI providers' : 'Verify answer against multiple AI providers'}>
              <Button size="small" variant="outlined" startIcon={<VerifiedUserIcon />}
                style={{ textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed' }}
                onClick={handleVerifyAnswer}>
                {conflictInfo ? 'Re-verify' : 'Verify Answer'}
              </Button>
            </Tooltip>
          )}
          {verifying && (
            <Button size="small" variant="outlined" disabled
              startIcon={<CircularProgress size={14} />}
              style={{ textTransform: 'none' }}>
              Verifying...
            </Button>
          )}
          {/* Inline panel buttons when conflict exists */}
          {isPending && hasPermission('QA Actions', 'Resolve Answer Conflict') && (
            <Button size="small" variant="contained" color="warning"
              sx={{ textTransform: 'none', ml: 1 }}
              onClick={() => onShowResolve(conflictInfo)}>
              Review & Resolve
            </Button>
          )}
          {isResolved && (
            <Button size="small" variant="outlined"
              sx={{ textTransform: 'none', ml: 1 }}
              onClick={() => onShowDetail(conflictInfo)}>
              View Details
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// A grid that renders nothing when it has no rows leaves a blank white tab, which reads as "this
// feature is broken" rather than "there is nothing here". Every data tab goes through this so the
// three states - still loading, failed, genuinely empty - are told apart on screen.
const TabState = ({ children, error }) => (
  <div style={{ padding: '24px 8px', color: error ? '#b3261e' : '#6b7280', fontSize: 14 }}>
    {children}
  </div>
);

const DataTab = ({ loaded, error, gridOptions, emptyMessage }) => {
  if (error) {
    return <TabState error>{error}</TabState>;
  }
  // Not every host wires every tab - the exam builder (ExamModal) renders this same viewer but
  // never fetches usage or feedback, so it passes no gridOptions at all. Stay blank there rather
  // than claiming an empty result we never asked for.
  if (!gridOptions) {
    return null;
  }
  // `loaded` false means a fetch is in flight. Checking it before rowData matters when the user
  // pages to the next question: gridOptions still holds the previous question's rows until the
  // new response lands, and showing those would attribute them to the wrong question.
  if (loaded === false || !gridOptions.columnDefs) {
    return <TabState>Loading…</TabState>;
  }
  if ((gridOptions.rowData || []).length === 0) {
    return <TabState>{emptyMessage}</TabState>;
  }
  return <Grid gridOptions={gridOptions} />;
};

const questionTabList = (props) => [{

  label: 'Detail',
  content: <><div style={{ marginTop: "15px" }}>
    {(props?.answers || []).map((ans, idx) => (
      <p style={{ display: "flex" }}>
        {String.fromCharCode(65 + idx)} &nbsp;
        <div
          style={{ fontWeight: ans?.correct ? "900" : "400" }}
          dangerouslySetInnerHTML={{ __html: ans?.answer }}
        />
      </p>
    ))}
  </div>
    <div>
      <p>
        <p style={{ textDecoration: "underline" }}>
          Explanation:
        </p>
        <div
          style={{ padding: "10px" }}
          dangerouslySetInnerHTML={{ __html: props?.explanation }}
        />
      </p>
    </div>
    <p style={{ display: "flex" }}>
      <p style={{ textDecoration: "underline" }}>References:</p>
      <div
        style={{ padding: "10px" }}
        dangerouslySetInnerHTML={{ __html: props?.reference }}
      />
    </p>
    <AIExplanationSection questionId={props?.questionId} onShowResolve={props?.onShowResolve} onShowDetail={props?.onShowDetail} refreshRef={props?.refreshRef} />
  </>
}, {
  label: 'History',
  content: <>
    {props?.displayAudit && (props?.auditGridOptions?.rowData || []).length > 0 && <Grid gridOptions={props?.auditGridOptions} />}
  </>
},
{
  label: 'Where used',
  content: <DataTab
    loaded={props?.showWhereUsedGrid}
    error={props?.whereUsedError}
    gridOptions={props?.whereUsedPopupgridOptions}
    emptyMessage="This question is not used in any exam, test or question bank module."
  />
},
{
  label: 'Student feedback',
  disabled: false,
  // Gated on feedbackGrid, not showWhereUsedGrid - these are two independent requests and a slow
  // or failed "where used" call used to blank this tab out as well.
  content: <DataTab
    loaded={props?.feedbackGrid}
    error={props?.feedbackError}
    gridOptions={props?.feedbackPopupgridOptions}
    emptyMessage="No student feedback reported for this question."
  />
},
]

export default function QuestionViewer(props) {

  const [showTable, setShowTable] = useState(true)
  const [loaded, setLoaded] = useState(false)
  let [auditGridOptions, setAuditGridOptions] = useState({});
  let [displayAudit, setDisplayAudit] = useState(false);
  let auditGridApi = useRef();
  const [variationsDialogOpen, setVariationsDialogOpen] = useState(false);
  const [questionDataLocal, setQuestionDataLocal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Inline panel swap state — 'normal' | 'resolve' | 'detail'
  const [viewMode, setViewMode] = useState('normal');
  const [activeConflict, setActiveConflict] = useState(null);
  const refreshConflictRef = useRef(null);

  const canRestyleQuestions = hasPermission('QA Actions', 'Restyle Questions');
  const canGenerateVariations = hasPermission('QA Actions', 'Generate Variations');
  const showVariationsButton = canRestyleQuestions || canGenerateVariations;

  useEffect(() => {
    setLoaded(false)
    setShowTable(false)
    setShowTable(true)
    setQuestionDataLocal(null)
    setRefreshKey(0)
    if(props.questionId){
    get(apiPaths.questionstatusaudit, { questionId: props?.questionId, tenantId: getUserDetails().tenantId })
      .then((res) => {
        setDisplayAudit(false);
        if (res?.result) {
          auditGridOptions.columnDefs = auditColumnDefs;
          res.result.sort((e1, e2) => e2.auditId - e1.auditId);
          auditGridOptions.rowData = res?.result;
          auditGridOptions.paginationPageSize = 20;
          auditGridOptions.pagination = true;
          auditGridOptions.domLayout = '';
          auditGridOptions.onGridReady = (params) => {
            auditGridApi.current = params.api;
          };
          setTimeout(() => auditGridApi?.current?.setRowData(res?.result));
          setDisplayAudit(true);
        }
      })
      .catch((ele) => {
      });
    setTimeout(() => setLoaded(true), 500)
    }
  }, [props?.questionId])

  // Re-fetch question data when refreshKey changes (after verification/resolution updates)
  useEffect(() => {
    if (refreshKey > 0 && props?.questionId) {
      get(apiPaths.questionbyid, { id: props.questionId })
        .then(({ result }) => {
          if (result) setQuestionDataLocal(result);
        })
        .catch(() => {});
    }
  }, [refreshKey, props?.questionId]);

  // Reset viewMode when question changes
  useEffect(() => {
    setViewMode('normal');
    setActiveConflict(null);
  }, [props?.questionId]);

  // Handlers for inline panel swap
  const handleShowResolve = (conflict) => {
    setActiveConflict(conflict);
    setViewMode('resolve');
  };

  const handleShowDetail = (conflict) => {
    setActiveConflict(conflict);
    setViewMode('detail');
  };

  const handlePanelCancel = () => {
    setViewMode('normal');
    setActiveConflict(null);
    // Refresh conflict info in AIExplanationSection
    if (refreshConflictRef.current) refreshConflictRef.current();
    setRefreshKey(k => k + 1);
  };

  const handleResolve = async (conflictId, resolvedAnswer, resolvedExplanation, notes) => {
    const data = await putInstituteAppData(
      apiPaths.answerConflicts + '/' + conflictId + '/resolve',
      { data: { resolvedAnswer, resolvedExplanation, notes }, headers: { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email } }
    );
    if (data?.success) {
      if (refreshConflictRef.current) refreshConflictRef.current();
      setRefreshKey(k => k + 1);
    } else {
      throw new Error(data?.message || 'Resolution failed');
    }
  };

  const handleSwitchExplanation = async (qId, explanation, source) => {
    if (!explanation?.trim()) throw new Error('Explanation cannot be empty');
    const data = await putInstituteAppData(
      apiPaths.answerConflictsUpdateExplanation + '/' + qId,
      { data: { explanation, notes: 'Switched to ' + source + ' from question viewer' }, headers: { tenantId: getUserDetails().tenantId, userEmail: getUserDetails().email } }
    );
    if (!data?.success) throw new Error(data?.message || 'Failed to update explanation');
  };

  // Merge: use locally refreshed data if available, otherwise use props
  const mergedProps = questionDataLocal ? { ...props, ...questionDataLocal } : props;

  if (!props?.questionId) {
    return <>{loaded && <div style={{ fontSize: '20px', display: 'flex', flexFlow: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <p><AnnouncementIcon fontSize="large" /></p>
      <p>No question present with this question Id</p>
    </div>}</>
  }
  // Render inline panel when in resolve/detail mode
  if (viewMode === 'resolve' && activeConflict) {
    return (
      <div style={{ background: "white", borderRadius: "15px", padding: "10px" }}>
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <ResolvePanel
            conflict={activeConflict}
            onCancel={handlePanelCancel}
            onResolve={handleResolve}
          />
        </div>
      </div>
    );
  }

  if (viewMode === 'detail' && activeConflict) {
    return (
      <div style={{ background: "white", borderRadius: "15px", padding: "10px" }}>
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <DetailPanel
            conflict={activeConflict}
            onCancel={handlePanelCancel}
            onSwitchExplanation={handleSwitchExplanation}
            onCorrectAnswer={handleResolve}
            onRefresh={() => { if (refreshConflictRef.current) refreshConflictRef.current(); setRefreshKey(k => k + 1); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "white", borderRadius: "15px", padding: "10px" }}>
      <div style={{ display: "flex", minHeight: "72vh" }}>
        <div className="col-sm-9" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <div style={{ width: "100%" }}>

            <div>
              <div style={{ margin: "10px 0" }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flex: 1 }}>{mergedProps?.questionId}. <div dangerouslySetInnerHTML={{ __html: mergedProps?.question }} /></div>
                  {showVariationsButton && (
                    <Tooltip title="AI Question Variations — Restyle or Generate New">
                      <Button size="small" variant="outlined"
                        startIcon={<TuneIcon />}
                        onClick={() => setVariationsDialogOpen(true)}
                        sx={{
                          textTransform: 'none', ml: 1, whiteSpace: 'nowrap',
                          borderColor: '#6366f1', color: '#6366f1', fontSize: 11,
                          '&:hover': { backgroundColor: '#6366f1', color: '#fff' },
                        }}>
                        AI Variations
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
            <BasicTabs tabList={questionTabList({
              ...mergedProps,
              displayAudit: displayAudit,
              auditGridOptions: auditGridOptions,
              onQuestionUpdated: () => setRefreshKey(k => k + 1),
              onShowResolve: handleShowResolve,
              onShowDetail: handleShowDetail,
              refreshRef: refreshConflictRef,
            })} />

          </div>
        </div>
        <Divider orientation="vertical" flexItem />
        <BasicTabs tabList={tabList(mergedProps)} />

      </div>
      {props?.selectable && <><Checkbox onChange={(e) => {
        props.onSelect(props?.questionId, e.target.checked)
      }} checked={props?.manualQuestions.includes(props?.questionId)} /> Question is selected </>}
      <Divider light />
      {sessionStorage.getItem("routeFrom") !== 'dashboard' && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "16px",
          }}
        >
          <Button
            type="submit"
            variant="outlined"
            disabled={!props?.prevQuestion || +props?.prevQuestion <= 0 || props?.prevQuestion === props?.questionId}
            onClick={() => {
              if (props?.prevQuestion && +props.prevQuestion > 0) {
                props.setId(props.prevQuestion);
              }
            }}
            color="primary"
          >
            Previous
          </Button>
          <Typography variant="caption" display="block" gutterBottom>
            QuestionId: {props?.questionId}
          </Typography>

          <Button
            type="submit"
            variant="outlined"
            color="primary"
            disabled={+(props?.count || 0) === +props?.questionId || !props?.nxtQuestion || +props?.nxtQuestion <= 0 || props?.nxtQuestion === props?.questionId}
            onClick={() => {
              if (props?.nxtQuestion && +props.nxtQuestion > 0) {
                props.setId(props.nxtQuestion);
              }
            }}
          >
            Next
          </Button>
        </div>
      )}

      {sessionStorage.getItem("routeFrom") === 'dashboard' && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "16px",
          }}
        >
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            disabled={+(props?.count || 0) === +props?.questionId || !props?.nxtQuestion || +props?.nxtQuestion <= 0}
            onClick={() => {
              if (props?.nxtQuestion && +props.nxtQuestion > 0) {
                props.setId(props.nxtQuestion);
              }
            }}
          >
            Previous
          </Button>

          <Typography variant="caption" display="block" gutterBottom>
            QuestionId: {props?.questionId}
          </Typography>

          <Button
            type="submit"
            variant="outlined"
            disabled={!props?.prevQuestion || +props?.prevQuestion <= 0 || props?.prevQuestion === props?.questionId}
            onClick={() => {
              if (props?.prevQuestion && +props.prevQuestion > 0) {
                props.setId(props.prevQuestion);
              }
            }}
            color="primary"
          >
            Next
          </Button>
        </div>
      )}

      {/* AI Variations Dialog */}
      {showVariationsButton && (
        <QuestionVariationsDialog
          open={variationsDialogOpen}
          onClose={() => setVariationsDialogOpen(false)}
          questionId={props?.questionId}
          questionText={props?.question}
          onQuestionUpdated={() => {
            // Reload the question viewer to reflect changes
            if (props?.setId) props.setId(props?.questionId);
          }}
        />
      )}
    </div>
  );
}
